import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { SubPageHeader } from "@/components/SubPageHeader";
import { Save, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  company_name: "", logo_url: "",
  work_start_time: "10:45", work_end_time: "20:45",
  lunch_duration: 60, grace_period: 15, regularization_window: 3,
  sundays_optional: true, backup_email: "", backup_whatsapp: "",
  hr_can_edit_employees: false, hr_can_edit_attendance: false, hr_can_edit_holidays: false,
};

export default function SettingsPage() {
  const { user } = useAuth();
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) setForm({ ...empty, ...settings }); }, [settings]);

  const isAdmin = user?.role === "admin";
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try { await api.patch("/settings", form); toast.success("Saved"); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  useEffect(() => { setTestEmail(user?.email || form?.backup_email || ""); }, [user, form?.backup_email]);
  const sendSample = async () => {
    if (!testEmail) { toast.error("Enter an email"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/daily-summary/test", { email: testEmail });
      toast.success(`Sample sent to ${data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setTesting(false); }
  };

  const inputCls = "w-full h-11 px-3 rounded-xl text-sm disabled:opacity-60";
  const inputStyle = { background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" };

  return (
    <div className="min-h-[100svh]" style={{ background: "#0A0D14", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="settings-page">
      <SubPageHeader title="Settings" />
      <div className="px-4 py-4 space-y-4">
        <section className="rounded-2xl p-4 space-y-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Company</div>
          <div>
            <label className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Company Name</label>
            <input data-testid="settings-company" disabled={!isAdmin} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputCls} style={inputStyle} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Logo URL</label>
            <input data-testid="settings-logo" disabled={!isAdmin} value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className={inputCls} style={inputStyle} placeholder="https://…" />
          </div>
        </section>

        <section className="rounded-2xl p-4 space-y-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Work Policy</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Start</label>
              <input data-testid="settings-start" type="time" disabled={!isAdmin} value={form.work_start_time} onChange={(e) => set("work_start_time", e.target.value)} className={inputCls} style={inputStyle} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>End</label>
              <input data-testid="settings-end" type="time" disabled={!isAdmin} value={form.work_end_time} onChange={(e) => set("work_end_time", e.target.value)} className={inputCls} style={inputStyle} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Grace (min)</label>
              <input data-testid="settings-grace" type="number" disabled={!isAdmin} value={form.grace_period} onChange={(e) => set("grace_period", Number(e.target.value))} className={inputCls} style={inputStyle} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Reg Window (days)</label>
              <input data-testid="settings-reg-window" type="number" disabled={!isAdmin} value={form.regularization_window} onChange={(e) => set("regularization_window", Number(e.target.value))} className={inputCls} style={inputStyle} />
            </div>
          </div>
          <label className="flex items-center justify-between pt-2">
            <span className="text-sm" style={{ color: "#E8ECFF" }}>Sundays Optional</span>
            <input type="checkbox" disabled={!isAdmin} checked={!!form.sundays_optional} onChange={(e) => set("sundays_optional", e.target.checked)} data-testid="settings-sundays" className="w-5 h-5 accent-[#4F8EF7]" />
          </label>
        </section>

        <section className="rounded-2xl p-4 space-y-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Backup Destinations</div>
          <div>
            <label className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Backup Email</label>
            <input data-testid="settings-backup-email" disabled={!isAdmin} value={form.backup_email || ""} onChange={(e) => set("backup_email", e.target.value)} className={inputCls} style={inputStyle} placeholder="admin@company.com" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>WhatsApp (with country code)</label>
            <input data-testid="settings-backup-wa" disabled={!isAdmin} value={form.backup_whatsapp || ""} onChange={(e) => set("backup_whatsapp", e.target.value)} className={inputCls} style={inputStyle} placeholder="919876543210" />
          </div>
        </section>

        {isAdmin && (
          <section className="rounded-2xl p-4 space-y-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid="hr-permissions-card">
            <div>
              <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>HR Manager Permissions</div>
              <div className="text-[11px] mt-1" style={{ color: "#7A85A3" }}>
                Grant your HR Managers correction rights. Every HR edit is audited and included in the 11&nbsp;AM daily summary email.
              </div>
            </div>
            {[
              ["hr_can_edit_employees", "Edit employee details", "Edit/delete employees other than themselves"],
              ["hr_can_edit_attendance", "Edit / correct attendance", "Change or delete any check-in/check-out row"],
              ["hr_can_edit_holidays", "Edit holidays", "Add or remove company holidays"],
            ].map(([key, label, sub]) => (
              <label key={key} className="flex items-center justify-between gap-3 cursor-pointer">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold" style={{ color: "#E8ECFF" }}>{label}</div>
                  <div className="text-[11px]" style={{ color: "#7A85A3" }}>{sub}</div>
                </div>
                <input
                  type="checkbox"
                  data-testid={`toggle-${key.replace(/_/g, "-")}`}
                  checked={!!form[key]}
                  onChange={(e) => set(key, e.target.checked)}
                  className="w-6 h-6 accent-[#22D3A5] flex-shrink-0"
                />
              </label>
            ))}
          </section>
        )}

        {isAdmin && (
          <section className="rounded-2xl p-4 space-y-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid="sample-email-card">
            <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Test Daily Summary Email</div>
            <div className="text-[11px]" style={{ color: "#7A85A3" }}>
              Sends a sample of yesterday&rsquo;s attendance report to the email below — instantly. Use this to verify your inbox setup before 11&nbsp;AM IST.
            </div>
            <input
              data-testid="sample-email-input"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className={inputCls}
              style={inputStyle}
              placeholder="you@company.com"
            />
            <button
              data-testid="send-sample-email-btn"
              onClick={sendSample}
              disabled={testing}
              className="w-full h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #4F8EF7, #22D3A5)", color: "#fff" }}
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {testing ? "Sending…" : "Send Sample to My Inbox"}
            </button>
          </section>
        )}

        {isAdmin && (
          <button data-testid="save-settings" onClick={save} disabled={saving} className="w-full h-12 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "#4F8EF7", color: "#fff" }}>
            <Save size={16} /> {saving ? "Saving…" : "Save Settings"}
          </button>
        )}
      </div>
    </div>
  );
}
